import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { FC, Fragment } from 'react';

import Chevron from '@/assets/icons/chevron-right.svg';
import Close from '@/assets/icons/close.svg';
import Exit from '@/assets/icons/exit.svg';
import Rocket from '@/assets/icons/rocket.svg';
import Warning from '@/assets/icons/warning.svg';
import ConfirmModalButtons from '@/components/app/confirmModalButtons/ConfirmModalButtons';
import { EventPageView, PAGE_VIEW_TITLE, sendPageView } from '@/utils/gtm';
import { logError } from '@/utils/log';
import { toastError, toastSuccess } from '@/utils/notification';
import { getFullName } from '@proprioo/hokkaido';
import {
  Button,
  ButtonAppearance,
  IconPosition,
  useModal
} from '@proprioo/salatim';

import {
  ButtonsWrapper,
  ExistingCustomer,
  ExistingMessage,
  WarningWrapper
} from './ExistingCustomerWarning.styles';
import { ExistingCustomerProps } from './interfaces';
import { mergeCustomer } from './utils';

const ExistingCustomerEdit: FC<Omit<ExistingCustomerProps, 'onSelect'>> = ({
  fetchedCustomer,
  customer,
  onCancel,
  redirectToCustomer
}) => {
  const { asPath } = useRouter();
  const { t } = useTranslation();

  const { openModal, closeModal, createModal, toggleLoader } = useModal({
    onOpen: () =>
      sendPageView(
        { path: asPath, title: PAGE_VIEW_TITLE.MODAL_CUSTOMER_EXISTING_EDIT },
        EventPageView.MODAL_OPENING
      )
  });

  const replacedCustomer = getFullName(customer.firstName, customer.lastName);
  const newCustomer = getFullName(
    fetchedCustomer?.user.firstName,
    fetchedCustomer?.user.lastName
  );

  const mergeUser = async () => {
    if (fetchedCustomer?.user) {
      toggleLoader();

      try {
        await mergeCustomer(fetchedCustomer.user.id, customer.id);
        toastSuccess('mergeCustomerSuccess');
        if (redirectToCustomer) {
          redirectToCustomer(fetchedCustomer.user.id);
        }
      } catch (error) {
        logError(`Failed to merge user on customer ${customer.id}`, {
          customerToReplace: customer.id,
          customerToUpdate: fetchedCustomer.user.id,
          error
        });
        toastError('mergeCustomerError');
      } finally {
        closeModal();
      }
    }
  };

  const openCustomer = () => {
    window.open(`/customer/${fetchedCustomer?.user.id}`, '_blank');
  };

  return (
    <ExistingCustomer>
      <WarningWrapper>
        <Warning aria-label={t('warning')} />
      </WarningWrapper>
      {fetchedCustomer && (
        <ExistingMessage
          data-test="existing-customer-text"
          dangerouslySetInnerHTML={{
            __html: t('existingEmailError', {
              fullName: newCustomer,
              nbBuy: t('buyerProjectsCount', {
                count: fetchedCustomer.countAlerts
              }),
              nbSell: t('sellerProjectCount', {
                count: fetchedCustomer.countOpportunities
              })
            })
          }}
        />
      )}
      <ButtonsWrapper>
        <Button
          data-test="existing-customer-back"
          label={t('cancel')}
          appearance={ButtonAppearance.SECONDARY}
          iconPosition={IconPosition.LEFT}
          icon={<Close />}
          onClick={() => onCancel()}
        />
        <Button
          data-test="see-customer-profile"
          label={t('seeAccount')}
          appearance={ButtonAppearance.SECONDARY}
          iconPosition={IconPosition.LEFT}
          icon={<Exit />}
          onClick={openCustomer}
        />
        <Button
          data-test="merge-customer-profile"
          label={t('mergeCustomer')}
          icon={<Chevron />}
          onClick={() => openModal()}
        />
      </ButtonsWrapper>
      <Fragment>
        {createModal({
          children: (
            <div data-test="modal-cancel">
              <ConfirmModalButtons
                data-test="cancel-meeting-modal-buttons"
                onAccept={mergeUser}
                onReject={closeModal}
              />
            </div>
          ),
          icon: <Rocket />,
          isAlternative: true,
          text: t('mergeCustomerConfirmation', {
            newCustomer,
            replacedCustomer
          }),
          title: t('mergeCustomer')
        })}
      </Fragment>
    </ExistingCustomer>
  );
};

export default ExistingCustomerEdit;
